import moment from 'moment';
import { useContext, useEffect, useRef, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled, { ThemeContext } from 'styled-components';

import * as graphql from '@treams/treams-network';
import { Button, Input, tokens } from '@treams/treams-ui';

const Pill = styled.label<{ $color: string }>`
	font-size: 11px;
	color: ${(props) => props.$color};
	border: 2px solid ${(props) => props.$color};
	background-color: ${(props) => props.$color}20;
	border-radius: 3px;

	padding: 1px 2px;
	display: block;
	width: 60px;
	text-align: center;
`;

function LogLevelPill({ logLevel }: { logLevel: graphql.ConnectorLogLevel }) {
	const theme = useContext(ThemeContext);
	const { colors } = theme;

	const logLevelColors = {
		[graphql.ConnectorLogLevel.Info]: colors.blue,
		[graphql.ConnectorLogLevel.Warning]: colors.yellow,
		[graphql.ConnectorLogLevel.Error]: colors.red,
	};

	const logLevelText = {
		[graphql.ConnectorLogLevel.Info]: 'Info',
		[graphql.ConnectorLogLevel.Warning]: 'Warning',
		[graphql.ConnectorLogLevel.Error]: 'Error',
	};

	return <Pill $color={logLevelColors[logLevel]}>{logLevelText[logLevel]}</Pill>;
}

const ConnectorLogListItem = styled(Flex)`
	font-family: monospace;
	font-weight: 600;
	font-size: 12px;
	color: ${(props) => props.theme.colors.black};
	white-space: nowrap;
	min-width: fit-content;
	width: 100%;
`;

const LoadMoreItems = styled.button`
	background: none;
`;

const NoMoreItems = styled(Flex)`
	color: ${(props) => props.theme.colors.black};
	font-size: 12px;
	font-weight: 600;
	font-family: monospace;
	color: ${(props) => props.theme.colors.black}80;
`;

const HorizontalScrollContainer = styled(Flex)`
	overflow-x: auto;
`;

const ConnectorLogList = styled(Flex)`
	width: 100%;
	min-width: fit-content;

	${ConnectorLogListItem}:nth-child(odd) {
		background-color: ${tokens.surface.secondary};
	}

	${NoMoreItems} {
		display: block;
	}

	${LoadMoreItems}, ${NoMoreItems} {
		background-color: ${tokens.surface.secondary};
		flex: 1;
		border-top: 1px solid ${tokens.text.tertiary};
		width: 100%;
		padding: 18px 8px;
		text-align: center;
	}
`;

export default function ConnectorLogsContainer() {
	const resultsPerPage = 1000;

	const [pagination, setPagination] = useState<{ first?: number; after?: string }>({
		first: resultsPerPage,
	});
	const [logDate, setLogDate] = useState<string>(moment().format('YYYY-MM-DD'));
	const [minimumLogLevel, setMinimumLogLevel] = useState<graphql.ConnectorLogLevel>(
		graphql.ConnectorLogLevel.Info,
	);
	const [items, setItems] = useState<graphql.ConnectorLog[]>([]);
	const [endCursor, setEndCursor] = useState<string | undefined>();
	const [hasNextPage, setHasNextPage] = useState<boolean>(false);

	const logsEndRef = useRef<HTMLDivElement | null>(null);

	const { data, loading } = graphql.useConnectorLogsQuery({
		variables: {
			date: logDate,
			minimumLogLevel,
			...pagination,
		},
		fetchPolicy: 'network-only',
	});

	const loadMoreItems = () => {
		setPagination({ first: resultsPerPage, after: endCursor });
	};

	const changeFilters = (date: string, logLevel: graphql.ConnectorLogLevel) => {
		setItems([]);
		setEndCursor(undefined);
		setHasNextPage(false);
		setLogDate(date);
		setMinimumLogLevel(logLevel);
		setPagination({
			first: resultsPerPage,
		});
	};

	const changeDay = (offset: number) => {
		changeFilters(
			moment(logDate, 'YYYY-MM-DD').add(offset, 'days').format('YYYY-MM-DD'),
			minimumLogLevel,
		);
	};

	useEffect(() => {
		if (data) {
			setItems(items.concat(data.connectorLogs.edges.map(({ node }) => node)));
			setEndCursor(data.connectorLogs.pageInfo.endCursor ?? undefined);
			setHasNextPage(data.connectorLogs.pageInfo.hasNextPage);
			setTimeout(() => {
				logsEndRef?.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
			}, 100);
		}
	}, [data]);

	return (
		<Box>
			<Flex flexDirection="row" justifyContent="center" alignItems="center" my={3}>
				<Button
					small
					onClick={() => changeDay(-1)}
					disabled={loading}
					title="Previous day"
					type="transparent"
				/>
				<Box mx={2}>
					<Input.DatePicker
						value={moment(logDate, 'YYYY-MM-DD').toDate()}
						onChange={(startDate) => {
							changeFilters(moment(startDate).format('YYYY-MM-DD'), minimumLogLevel);
						}}
						disabled={loading}
					/>
				</Box>
				<Button
					onClick={() => changeDay(1)}
					disabled={loading || moment(logDate).isAfter(moment().subtract(1, 'day').endOf('day'))}
					title="Next day"
					small
					type="transparent"
				/>
			</Flex>

			<HorizontalScrollContainer>
				<ConnectorLogList flexDirection="column">
					{!loading && items.length === 0 && (
						<ConnectorLogListItem p={2}>No items to show for this date</ConnectorLogListItem>
					)}
					{items.map((node, index) => (
						<ConnectorLogListItem key={index} flexDirection="row" alignItems="center" py={1}>
							<Box width="68px" ml={2}>
								{moment(node.timestamp).format('HH:mm:ss')}
							</Box>
							<Box width="68px">
								<LogLevelPill logLevel={node.logLevel} />
							</Box>
							<Box width={10 / 12} mr={2}>
								{node.message}
							</Box>
						</ConnectorLogListItem>
					))}
					{!loading && hasNextPage && (
						<LoadMoreItems onClick={loadMoreItems}>Click here to load more items</LoadMoreItems>
					)}
					{!loading && !hasNextPage && <NoMoreItems>No more items</NoMoreItems>}
				</ConnectorLogList>
			</HorizontalScrollContainer>
			<div ref={logsEndRef} />
		</Box>
	);
}
